/**
 * OTP Gate Component
 *
 * Handles patient identity verification via OTP before allowing access
 * to patient action links. This is the entry point for all /p/:token/* routes.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Mail, Phone, ShieldCheck, AlertCircle, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/services/api';
import { cn } from '@/lib/utils';

interface PatientActionLink {
  id: string;
  purpose: string;
  clinic: {
    id: string;
    name: string;
  };
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface OTPGateProps {
  token: string;
  onVerified: (sessionToken: string, patientId: string, linkData: PatientActionLink) => void;
  onError: (error: string) => void;
}

const OTPGate: React.FC<OTPGateProps> = ({ token, onVerified, onError }) => {
  const [step, setStep] = useState<'loading' | 'identity' | 'contact' | 'otp' | 'verified'>(
    'loading'
  );
  const [linkData, setLinkData] = useState<PatientActionLink | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [requiresOtp, setRequiresOtp] = useState(false);

  // Identity verification form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Contact method selection
  const [contactMethod, setContactMethod] = useState<'email' | 'sms'>('email');
  const [contactValue, setContactValue] = useState('');

  // OTP input
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Validate the link on mount
  useEffect(() => {
    validateLink();
  }, [token]);

  const validateLink = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/public/patient-link/${token}`);

      if (response.data.success) {
        setLinkData(response.data.data.patientActionLink);
        setSessionToken(token);
        setRequiresOtp(response.data.data.requires_otp);

        if (response.data.data.requires_otp) {
          // Need identity verification first
          setStep('identity');
        } else {
          // Already linked to a patient, just need OTP
          setStep('contact');
        }
      }
    } catch (err: any) {
      console.error('Link validation error:', err);
      const message = err.response?.data?.error?.message || 'This link is invalid or has expired';
      setError(message);
      onError(message);
      setStep('loading');
    } finally {
      setIsLoading(false);
    }
  };

  const handleIdentitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Just collect identity info, we'll verify it with OTP
    setStep('contact');
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sessionToken) {
      setError('Session expired. Please refresh the page.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await apiClient.post(`/public/patient-link/${token}/request-otp`, {
        contact_method: contactMethod,
        contact_value: contactValue,
      });
      toast({
        title: 'Verification Code Sent',
        description: `We've sent a 6-digit code to your ${contactMethod === 'email' ? 'email' : 'phone'}`,
      });

      setStep('otp');
    } catch (err: any) {
      console.error('OTP request error:', err);
      setError(err.response?.data?.error?.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').split('').slice(0, 6);
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);

      // Focus last filled input or next empty
      const nextIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
    } else {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    if (!sessionToken) {
      setError('Session expired. Please refresh the page.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.post(`/public/patient-link/${token}/verify-otp`, {
        session_token: sessionToken,
        otp: otpCode,
        patient_details: requiresOtp
          ? {
              first_name: firstName,
              last_name: lastName,
              dob: dob,
              zip_code: zipCode,
            }
          : undefined,
      });

      if (response.data.success) {
        setStep('verified');
        toast({
          title: 'Identity Verified',
          description: 'You can now continue to your requested action',
        });

        // Small delay to show success state
        setTimeout(() => {
          onVerified(sessionToken, response.data.data.patient_id, linkData!);
        }, 1000);
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.error?.message || 'Invalid verification code');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsLoading(true);
      await apiClient.post(`/public/patient-link/${token}/request-otp`, {
        contact_method: contactMethod,
        contact_value: contactValue,
      });

      toast({
        title: 'Code Resent',
        description: 'A new verification code has been sent',
      });
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (step === 'loading' && isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Card className='w-full max-w-md border-slate-700 bg-slate-800 text-white'>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <Loader2 className='h-12 w-12 animate-spin text-brand-teal' />
            <p className='mt-4 text-slate-300'>Validating your link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error && step === 'loading') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Card className='w-full max-w-md border-slate-700 bg-slate-800 text-white'>
          <CardHeader className='text-center'>
            <AlertCircle className='mx-auto h-16 w-16 text-red-500' />
            <CardTitle className='mt-4 text-xl text-red-500'>Link Invalid</CardTitle>
            <CardDescription className='text-slate-400'>{error}</CardDescription>
          </CardHeader>
          <CardContent className='text-center'>
            <p className='text-sm text-slate-400'>
              If you believe this is an error, please contact your healthcare provider.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Identity verification step
  if (step === 'identity') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4'>
        <Card className='w-full max-w-md border-slate-700 bg-slate-800 text-white'>
          <CardHeader className='text-center'>
            <img src='/novamed_logo_white.svg' alt='NovaMed' className='mx-auto mb-4 h-8' />
            <CardTitle>Verify Your Identity</CardTitle>
            <CardDescription className='text-slate-400'>
              {linkData?.clinic?.name
                ? `${linkData.clinic.name} requires identity verification`
                : 'Please verify your identity to continue'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleIdentitySubmit} className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='firstName'>First Name</Label>
                  <Input
                    id='firstName'
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder='John'
                    required
                    className='border-slate-600 bg-slate-700 text-white'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='lastName'>Last Name</Label>
                  <Input
                    id='lastName'
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder='Doe'
                    required
                    className='border-slate-600 bg-slate-700 text-white'
                  />
                </div>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='dob'>Date of Birth</Label>
                <Input
                  id='dob'
                  type='date'
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                  required
                  className='border-slate-600 bg-slate-700 text-white'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='zipCode'>ZIP Code</Label>
                <Input
                  id='zipCode'
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value)}
                  placeholder='12345'
                  pattern='[0-9]{5}'
                  maxLength={5}
                  required
                  className='border-slate-600 bg-slate-700 text-white'
                />
              </div>
              {error && (
                <div className='flex items-center gap-2 text-sm text-red-400'>
                  <AlertCircle className='h-4 w-4' />
                  {error}
                </div>
              )}
              <Button
                type='submit'
                className='w-full bg-brand-teal hover:bg-brand-teal/80'
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                Continue <ArrowRight className='ml-2 h-4 w-4' />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Contact method selection step
  if (step === 'contact') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4'>
        <Card className='w-full max-w-md border-slate-700 bg-slate-800 text-white'>
          <CardHeader className='text-center'>
            <img src='/novamed_logo_white.svg' alt='NovaMed' className='mx-auto mb-4 h-8' />
            <CardTitle>Verify Your Contact</CardTitle>
            <CardDescription className='text-slate-400'>
              Choose how you'd like to receive your verification code
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRequestOtp} className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <Button
                  type='button'
                  variant={contactMethod === 'email' ? 'default' : 'outline'}
                  className={cn(
                    'h-16 flex-col gap-1',
                    contactMethod === 'email'
                      ? 'bg-brand-teal hover:bg-brand-teal/80'
                      : 'border-slate-600 bg-slate-700 hover:bg-slate-600'
                  )}
                  onClick={() => setContactMethod('email')}
                >
                  <Mail className='h-5 w-5' />
                  <span className='text-xs'>Email</span>
                </Button>
                <Button
                  type='button'
                  variant={contactMethod === 'sms' ? 'default' : 'outline'}
                  className={cn(
                    'h-16 flex-col gap-1',
                    contactMethod === 'sms'
                      ? 'bg-brand-teal hover:bg-brand-teal/80'
                      : 'border-slate-600 bg-slate-700 hover:bg-slate-600'
                  )}
                  onClick={() => setContactMethod('sms')}
                >
                  <Phone className='h-5 w-5' />
                  <span className='text-xs'>Text Message</span>
                </Button>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='contactValue' className='text-white'>
                  {contactMethod === 'email' ? 'Email Address' : 'Phone Number'}
                </Label>
                <Input
                  id='contactValue'
                  type={contactMethod === 'email' ? 'email' : 'tel'}
                  value={contactValue}
                  onChange={e => setContactValue(e.target.value)}
                  placeholder={contactMethod === 'email' ? 'you@example.com' : '(555) 123-4567'}
                  required
                  className='border-slate-600 bg-slate-700 text-black'
                />
              </div>
              {error && (
                <div className='flex items-center gap-2 text-sm text-red-400'>
                  <AlertCircle className='h-4 w-4' />
                  {error}
                </div>
              )}
              <Button
                type='submit'
                className='w-full bg-brand-teal hover:bg-brand-teal/80'
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                Send Verification Code <ArrowRight className='ml-2 h-4 w-4' />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // OTP input step
  if (step === 'otp') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4'>
        <Card className='w-full max-w-md border-slate-700 bg-slate-800 text-white'>
          <CardHeader className='text-center'>
            <img src='/novamed_logo_white.svg' alt='NovaMed' className='mx-auto mb-4 h-8' />
            <CardTitle>Enter Verification Code</CardTitle>
            <CardDescription className='text-slate-400'>
              We sent a 6-digit code to your {contactMethod === 'email' ? 'email' : 'phone'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyOtp} className='space-y-6'>
              <div className='flex justify-center gap-2'>
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={el => (otpInputRefs.current[index] = el)}
                    type='text'
                    inputMode='numeric'
                    maxLength={6}
                    value={digit}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(index, e)}
                    className='h-14 w-12 border-slate-600 bg-slate-700 text-center text-2xl font-bold text-black'
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              {error && (
                <div className='flex items-center justify-center gap-2 text-sm text-red-400'>
                  <AlertCircle className='h-4 w-4' />
                  {error}
                </div>
              )}
              <Button
                type='submit'
                className='w-full bg-brand-teal hover:bg-brand-teal/80'
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <ShieldCheck className='mr-2 h-4 w-4' />
                )}
                Verify Code
              </Button>
              <div className='text-center'>
                <button
                  type='button'
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className='text-sm text-brand-soft-teal hover:underline disabled:opacity-50'
                >
                  Didn't receive a code? Resend
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verified state
  if (step === 'verified') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4'>
        <Card className='w-full max-w-md border-slate-700 bg-slate-800 text-white'>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <div className='rounded-full bg-green-500/20 p-4'>
              <ShieldCheck className='h-12 w-12 text-green-500' />
            </div>
            <h2 className='mt-4 text-xl font-semibold'>Identity Verified</h2>
            <p className='mt-2 text-slate-400'>Redirecting you now...</p>
            <Loader2 className='mt-4 h-6 w-6 animate-spin text-brand-teal' />
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default OTPGate;
